import { ReactComponent as PDFIcon } from '../../icons/pdf.svg';
import { ReactComponent as XLSIcon } from '../../icons/xls.svg';
import { ReactComponent as XLSErrIcon } from '../../icons/xls-error.svg';
import { ReactComponent as CSVIcon } from '../../icons/csv.svg';
import { ReactComponent as CSVErrIcon } from '../../icons/csv-error.svg';
import { useTranslation } from 'react-i18next';
import './ManualUploadFileLink.scss';
import { useAppDispatch } from '../../hooks';
import { getS3Link } from '../../core/s3';
import { downloadFile } from '../../core/helpers';
import { addNotification } from '../../core/store/notificationsSlice';
import { DEFAULT_ERROR } from '../Notifications/Notification';
import MiniLoader from '../MiniLoader/MiniLoader';
import classNames from 'classnames';
import Button from '../Button';

interface Props {
  className?: string;
  icon?: JSX.Element;
  readyIcon?: JSX.Element;
  description?: JSX.Element;
  onDownload?: () => void;
  name: string;
  modifyName?: boolean;
  bucket: string;
  path: string;
  isLoading?: boolean;
  isError?: boolean;
}

export const ErrorFileIconsMap: {
  [key: string]: JSX.Element;
} = {
  pdf: <PDFIcon />,
  xls: <XLSErrIcon />,
  xlsx: <XLSErrIcon />,
  csv: <CSVErrIcon />,
};

export const FileIconsMap: {
  [key: string]: JSX.Element;
} = {
  pdf: <PDFIcon />,
  xls: <XLSIcon />,
  xlsx: <XLSIcon />,
  csv: <CSVIcon />,
};

export default function ManualUploadFileLink({
  name,
  className = '',
  icon,
  bucket,
  path,
  description,
  modifyName = true,
  isLoading,
  onDownload,
  isError,
  readyIcon,
}: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const handleDownload = async () => {
    const parts = `${bucket}/${path}`.split('/');

    try {
      const url = await getS3Link(parts[0], parts.slice(1).join('/'));
      downloadFile(url, parts[parts.length - 1]);
    } catch (error) {
      dispatch(
        addNotification({
          text: t(DEFAULT_ERROR),
          type: 'error',
        })
      );
    }
  };

  const modifiedName = name
    .split('.')
    .map((v, i, self) => {
      if (i === self.length - 2) {
        return v.replace(/_\d+$/, '..');
      }
      return v;
    })
    .join('.');

  return (
    <Button
      styling='naked'
      className={`text-start ${className} ManualUploadFileLink w-100`}
      onClick={onDownload || handleDownload}
      type='button'
    >
      <div className='d-flex align-items-start justify-content-between'>
        <div className='d-flex ManualUploadFileLink__info'>
          <span className='ManualUploadFileLink__icon rounded-circle me-2'>
            {icon ||
              (isError
                ? ErrorFileIconsMap[name.split('.').slice(-1)[0]!.toLowerCase()]
                : FileIconsMap[name.split('.').slice(-1)[0]!.toLowerCase()])}
          </span>
          <div>
            <div
              className={classNames('ManualUploadFileLink__name fw-600', {
                'pt-2': !description,
              })}
            >
              {modifyName ? modifiedName : name}
            </div>
            {description}
          </div>
        </div>
        {isLoading ? (
          <MiniLoader
            className='MiniLoader--sm d-flex align-items-center justify-content-center'
            style={{
              marginTop: !description ? 10 : 0,
            }}
          />
        ) : (
          readyIcon && <div className='align-self-center'>{readyIcon}</div>
        )}
      </div>
    </Button>
  );
}
